import * as THREE from "three";
import { AssetLibrary } from "./assets";
import type { EmblemLook } from "./skins/looks";
import { emblemArtUrl } from "./skins/emblemArt";
import { KitClock } from "./skins/kit/clock";
import { type FlagFrame, FlagMotes } from "./flagMotes";
import { FlagCloth, clothCoords, clothFrame } from "./flagCloth";

/** A pennant cloth in use: its canvas, its texture and the team colour it was made for (redrawn on an emblem change). */
interface Cloth { readonly ctx: CanvasRenderingContext2D; readonly tex: THREE.CanvasTexture; readonly team: THREE.Color }

/**
 * The game's emblem on the fortress pennant (owner play-test 2026-09-23: "the flag flapping in the wind is blank").
 *
 * Every fortress GLB (base_t1..3, level and hub alike — both load through AssetLibrary → HqMaterials.convert) draws
 * its pennant with the `<base>_team` material: a flat team colour, no texture, and UVs that stretch the whole tapered
 * cloth over the unit square (u along the cloth, v top to bottom of each column), so a picture on it would squash
 * toward the fly end. Instead of re-baking three GLBs, the pennant is dressed at load time:
 *  - `emblemUvs` replaces its UVs with a planar projection that puts a square emblem, undistorted, on the hoist half
 *    of the cloth — on BOTH faces (the cloth is two sheets, normals ±z), with u mirrored on the back sheet so the
 *    emblem reads the right way round from either side rather than as a real flag's mirror image;
 *  - `clothTexture` is a small canvas in the team colour with the emblem (art/interface/brand/emblem_256.png) drawn
 *    in the middle. Clamp-to-edge wrapping repeats its team-coloured border over the rest of the cloth, so the
 *    material's colour becomes white and the pennant keeps exactly its old colour everywhere but the emblem.
 * HqMaterials.convert calls both (`projectUvs` per mesh, `dressMaterial` once per material), so the level and the hub
 * fortress get it from the one load path. The flapping is the idle clip's rotation of the pennant node and is untouched. Until the image arrives (or if it
 * fails) the canvas is plain team colour — the old look, never black or blank.
 *
 * Flag emblems (FINISH_PLAN WP8): `setEmblem` swaps the worn emblem — its own cloth colour and painted shield
 * (skins/looks.ts, skins/emblemArt.ts) — by redrawing every live cloth canvas in place, so the hub's pennant changes at once and a
 * level loads with it; null is the stock crest on the team colour.
 */
export class FlagEmblem {
  /**
   * The emblem's square side as a fraction of the cloth's height at the hoist. The shield fills ~84 % of the square's
   * height, so this is as big as it goes with its point still clear of the tapered bottom edge (test/flagEmblem.test.ts):
   * the pennant is only a few dozen screen px from the game camera, and a smaller shield read as a dark speck.
   */
  static readonly SizeOfHoist = 0.95;
  /** The emblem's centre, from the hoist edge, as a fraction of the cloth's length: the tall half, clear of the tapered tip. */
  static readonly CentreFromHoist = 0.3;
  /** Canvas side in px: the pennant is ~0.5 world units long, a few dozen screen px; the emblem PNG is 256². */
  static readonly TexturePx = 256;
  /** Keeps the emblem legible when the camera looks along the cloth (clamped to what the GPU offers). */
  static readonly Anisotropy = 4;
  static readonly Url = "./interface/brand/emblem_256.png";

  /** Loaded emblem art by URL (the stock crest and each worn emblem), shared by every cloth. */
  private static readonly images = new Map<string, Promise<HTMLImageElement | null>>();
  private static readonly cloths = new Set<Cloth>();
  private static worn: EmblemLook | null = null;
  /** The worn emblem (the flag flourish reads it every frame). */
  static wornLook(): EmblemLook | null { return FlagEmblem.worn; }

  /** Wears an emblem on every pennant, now and at every later load; null = the stock crest. */
  static setEmblem(look: EmblemLook | null): void {
    FlagEmblem.worn = look;
    FlagCloth.wear(look);
    for (const c of FlagEmblem.cloths) FlagEmblem.paint(c);
  }

  /**
   * UVs for a pennant `positions` (xyz triples, cloth in the local XY plane, pole at one x end) with `normalZ` the z
   * of each vertex's normal. The hoist is the taller x end. The emblem square is centred `CentreFromHoist` of the
   * length in from it, halfway between the (flat) top edge and the tapered bottom edge there. v runs up (a canvas
   * texture is flipped on upload, so v = 1 is the image's top row).
   */
  static emblemUvs(positions: ArrayLike<number>, normalZ: ArrayLike<number>): Float32Array {
    const n = normalZ.length;
    const { hoistX, flyX, hoistLow, flyLow, yMax } = clothFrame(positions);
    const side = (yMax - hoistLow) * FlagEmblem.SizeOfHoist;
    const t = FlagEmblem.CentreFromHoist;
    const cx = hoistX + (flyX - hoistX) * t;
    const cy = (yMax + hoistLow + (flyLow - hoistLow) * t) / 2;
    const uv = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      const facing = normalZ[i] >= 0 ? 1 : -1;       // front sheet: +x is screen-right; the back sheet sees it mirrored
      uv[i * 2] = 0.5 + facing * (positions[i * 3] - cx) / side;
      uv[i * 2 + 1] = 0.5 + (positions[i * 3 + 1] - cy) / side;
    }
    return uv;
  }

  /** Re-projects one pennant mesh's UVs for the emblem (every mesh on a `_team` material; clones share the geometry). */
  static projectUvs(mesh: THREE.Mesh): void {
    const g = mesh.geometry;
    const pos = g.getAttribute("position"), nrm = g.getAttribute("normal");
    if (!pos || !nrm) return;
    // read through the accessors: the GLBs are quantized (normalized int16), the raw arrays are not in model units
    const p = new Float32Array(pos.count * 3), nz = new Float32Array(pos.count);
    for (let i = 0; i < pos.count; i++) { p[i * 3] = pos.getX(i); p[i * 3 + 1] = pos.getY(i); p[i * 3 + 2] = pos.getZ(i); nz[i] = nrm.getZ(i); }
    g.setAttribute("uv", new THREE.BufferAttribute(FlagEmblem.emblemUvs(p, nz), 2));
    g.setAttribute("aCloth", new THREE.BufferAttribute(clothCoords(p), 4));      // the living cloth's coordinates (flagCloth.ts)
    // the worn emblem's flourish rides the cloth: a child of the pennant mesh, so every clone of the fortress has it
    // and it flaps with the pennant (a mesh with children stays out of the rigid-rig merge: +1 draw, the cloth's own)
    if (typeof document !== "undefined") mesh.add(FlagFlourish.make(p));
  }

  /** The team material wears the emblem cloth as its colour map (white base colour: the canvas carries the team colour). */
  static dressMaterial(mat: THREE.MeshStandardMaterial, team: THREE.Color): void {
    if (typeof document === "undefined") { mat.color.copy(team); return; }     // no canvas (unit tests): the old flat colour
    mat.map = FlagEmblem.clothTexture(team);
    mat.color.set(0xffffff);
    FlagCloth.patch(mat);                                                          // the ripple and the worn banner's sheen
    mat.needsUpdate = true;
  }

  /**
   * A canvas texture of the team colour, with the emblem drawn over it once the image loads. One per material (not
   * shared): AssetLibrary.release disposes a model's textures with it, and the other tiers' pennants must not lose theirs.
   */
  static clothTexture(team: THREE.Color): THREE.CanvasTexture {
    const px = FlagEmblem.TexturePx;
    const cv = document.createElement("canvas");
    cv.width = cv.height = px;
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = FlagEmblem.Anisotropy;
    const cloth: Cloth = { ctx: cv.getContext("2d")!, tex, team: team.clone() };
    FlagEmblem.cloths.add(cloth);
    tex.addEventListener("dispose", () => FlagEmblem.cloths.delete(cloth));
    FlagEmblem.paint(cloth);
    return tex;
  }

  /**
   * Paints a cloth in the worn emblem: its cloth colour and (once loaded) its painted shield, or the team colour and
   * the crest. The cloth colour goes down at once, so a slow or failed image leaves a plain pennant, never a blank one.
   */
  private static paint(c: Cloth): void {
    const px = FlagEmblem.TexturePx, look = FlagEmblem.worn;
    c.ctx.fillStyle = look ? `#${look.cloth.toString(16).padStart(6, "0")}` : `#${c.team.getHexString(THREE.SRGBColorSpace)}`;
    c.ctx.fillRect(0, 0, px, px);
    c.tex.needsUpdate = true;
    void FlagEmblem.load(look ? emblemArtUrl(look.glyph) : AssetLibrary.resolveInlined(FlagEmblem.Url)).then((img) => {
      if (!img || FlagEmblem.worn !== look) return;        // another emblem was worn meanwhile
      c.ctx.drawImage(img, 0, 0, px, px);
      c.tex.needsUpdate = true;
    });
  }

  private static load(url: string): Promise<HTMLImageElement | null> {
    let p = FlagEmblem.images.get(url);
    if (!p) {
      p = new THREE.ImageLoader().loadAsync(url).catch((err) => {
        console.warn("flag emblem failed to load; the pennant stays plain", err);
        return null;
      });
      FlagEmblem.images.set(url, p);
    }
    return p;
  }
}

/**
 * A flag emblem's flourish (skins overhaul 2026-10-01: "none should just be a colour change"): on the fortress pennant,
 * the Ashen Banner's embers drifting up off the cloth, the Crowned Banner's gold crown on the pole top with a glinting
 * ruby, and the dated events' banners' motes (snow, blossom, sparks, chaff: flagMotes.ts). Built once per pennant mesh at load (FlagEmblem.projectUvs), in the cloth's own frame (cloth in local XY, pole
 * at the hoist end, y up), sized from the cloth; each frame it shows what the WORN emblem asks for (a live re-wear needs
 * no rebuild). Motes: a few additive points moved on the CPU (cheap: 10 points); frozen out by Reduced effects
 * (KitClock gates), the crown stays.
 */
export class FlagFlourish {
  /** Motes on the cloth at a time (embers, snow, blossom…: flagMotes.ts MOTE_STYLES). */
  static readonly Embers = 10;
  static readonly CrownName = "pennant_crown";
  /** A mote's point size, world units (a few px from the battle camera: big enough to catch the eye on the dark cloth). */
  static readonly EmberSize = 0.13;
  /** The crown's width and height as fractions of the cloth's height at the hoist. */
  static readonly CrownWidth = 0.95;
  static readonly CrownHeight = 0.7;
  /** The crown sits this far (cloth heights) above the cloth's top: on the pole's tip. */
  static readonly CrownLift = 0.06;
  /** The crown's slow turn, rad/s. */
  static readonly CrownTurn = 0.7;
  private static readonly gold = new THREE.MeshStandardMaterial({ color: 0xf2c14e, metalness: 1, roughness: 0.28, emissive: 0x6a4410, emissiveIntensity: 0.6, side: THREE.DoubleSide });
  static readonly ruby = new THREE.MeshStandardMaterial({ color: 0xc0182c, roughness: 0.15, emissive: 0xff2a3c, emissiveIntensity: 1.5 });

  /** The flourish group for a pennant whose cloth vertices are `p` (xyz triples, local units). */
  static make(p: ArrayLike<number>): THREE.Group {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity, zMid = 0;
    const n = p.length / 3;
    for (let i = 0; i < n; i++) { xMin = Math.min(xMin, p[i * 3]); xMax = Math.max(xMax, p[i * 3]); yMin = Math.min(yMin, p[i * 3 + 1]); yMax = Math.max(yMax, p[i * 3 + 1]); zMid += p[i * 3 + 2] / n; }
    const h = yMax - yMin, len = xMax - xMin;
    // the hoist: the end whose bottom hangs lower (as emblemUvs); the pole stands there
    let lowMin = Infinity, lowMax = Infinity;
    for (let i = 0; i < n; i++) { const x = p[i * 3], y = p[i * 3 + 1]; if (x - xMin < 0.02 * len) lowMin = Math.min(lowMin, y); if (xMax - x < 0.02 * len) lowMax = Math.min(lowMax, y); }
    const hoist = lowMax <= lowMin ? xMax : xMin, fly = hoist === xMax ? xMin : xMax;
    const g = new THREE.Group();
    g.name = "pennant_flourish";
    // the crown on the pole top: a zigzag band (five points), a ruby above
    const crown = new FlagCrown();
    const w = h * FlagFlourish.CrownWidth, ch = h * FlagFlourish.CrownHeight;
    // non-indexed, like the octahedron: RigidRig.merge takes every indexed mesh of the fortress into its skinned
    // draw, where the crown would stay drawn whatever the worn emblem (it does not look at this group's visibility)
    const band = new THREE.Mesh(FlagFlourish.crownGeometry(w / 2, ch).toNonIndexed(), FlagFlourish.gold);
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(w * 0.16), FlagFlourish.ruby);
    gem.position.y = ch * 1.15;
    crown.add(band, gem);
    crown.position.set(hoist, yMax + h * FlagFlourish.CrownLift, zMid);
    crown.visible = false;
    // the motes: a few points drifting off the cloth (rising embers, falling snow… as the worn emblem asks)
    const pos = new Float32Array(FlagFlourish.Embers * 3), col = new Float32Array(FlagFlourish.Embers * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const embers = new FlagMotes(geo, new THREE.PointsMaterial({ size: FlagFlourish.EmberSize, vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    embers.frustumCulled = false;
    embers.userData.flagFrame = { hoist, fly, yMin, h, zMid } satisfies FlagFrame;
    crown.name = FlagFlourish.CrownName;
    g.add(crown, embers);
    return g;
  }

  /** A crown band: a ring of radius `r`, its top edge rising into five points, `h` tall to the points' tips. */
  static crownGeometry(r: number, h: number): THREE.BufferGeometry {
    const points = 5, segs = points * 2, v: number[] = [], idx: number[] = [];
    for (let i = 0; i <= segs; i++) {
      const a = (i / segs) * Math.PI * 2, top = i % 2 === 0 ? h : h * 0.45;
      v.push(Math.cos(a) * r, 0, Math.sin(a) * r, Math.cos(a) * r, top, Math.sin(a) * r);
    }
    for (let i = 0; i < segs; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(v, 3));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  }
}

/**
 * The crown on the pole: shown while the worn emblem's flourish is "crown". Decided in `updateMatrixWorld`, which the
 * renderer runs on the scene before it culls (an onBeforeRender comes after: a frame late, and never for a one-frame
 * picture); a clone is a FlagCrown too, so each fortress decides its own.
 */
class FlagCrown extends THREE.Group {
  override updateMatrixWorld(force?: boolean): void {
    this.visible = FlagEmblem.wornLook()?.flourish === "crown";
    if (this.visible && KitClock.gates().motion) {
      const t = performance.now() / 1000;
      this.rotation.y = t * FlagFlourish.CrownTurn;                          // its facets catch the sun as it turns
      FlagFlourish.ruby.emissiveIntensity = 1.2 + 1.3 * Math.max(0, Math.sin(t * 2.3)) ** 6;
    }
    super.updateMatrixWorld(force);
  }
}
